import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render errors in the WMS subtree and displays a recovery screen.
 *
 * Without this boundary, a render bug in any WMS component takes the whole
 * app down — including the nav — leaving forklift operators with a blank
 * screen they can't recover from.
 */
export default class WmsErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[WmsErrorBoundary] render error', error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            p: 3,
          }}
        >
          <Paper
            sx={{
              p: 4,
              maxWidth: 480,
              textAlign: 'center',
              borderRadius: 3,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
            />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              This screen hit an unexpected error. Reloading usually fixes it.
              If it keeps happening, let the dev team know.
            </Typography>
            {this.state.error?.message && (
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  p: 1.5,
                  mb: 3,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  overflowX: 'auto',
                  fontFamily: 'monospace',
                }}
              >
                {this.state.error.message}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={this.handleReload}
            >
              Reload Screen
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
